import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo } from "react";
import { ServiceTemplateRecurrenceStatus } from "@common/types/serviceTemplateRecurrence";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { HStack } from "@design-system/HStack/HStack";
import { Button } from "@design-system/Button/Button";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { ServiceTemplateScheduleRow } from "../serviceTemplateSchedulesViewConfig";
import {
    ServiceTemplateScheduleCallbacks,
    ServiceTemplateScheduleTableOptions,
} from "../ServiceTemplateSchedulesList";

export const ServiceTemplateScheduleActionsCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, ServiceTemplateScheduleRow, string>) => {
        const meta = useViewTableMeta<
            ServiceTemplateScheduleCallbacks,
            ServiceTemplateScheduleTableOptions
        >(table);

        const { templateId } = row.original;
        const {
            onDeleteSchedule,
            onPauseSchedule,
            onResumeSchedule,
            onGoToTemplate,
            onOpenSchedule,
        } = meta.callbacks ?? {};

        // Shared between group and leaf rows so the label/behavior stays in sync.
        const goToTemplateItem = onGoToTemplate ? (
            <Menu.Item onClick={() => onGoToTemplate(templateId)}>
                Go to template
            </Menu.Item>
        ) : null;

        // Group rows represent N schedules at once, so there's no single
        // scheduleId to delete/pause/resume here — those actions live on each
        // expanded leaf row instead. "Go to template" still applies since every
        // member of the group shares the same templateId.
        if (row.original.rowType === "group") {
            if (!goToTemplateItem) {
                return null;
            }

            return (
                <ViewTableCell captureClicks>
                    <HStack gap={8} align="center" justify="end">
                        <Menu.More className={viewTableStyles.showOnHover}>
                            {goToTemplateItem}
                        </Menu.More>
                    </HStack>
                </ViewTableCell>
            );
        }

        const scheduleId = row.original.id;
        const isPaused =
            row.original.status === ServiceTemplateRecurrenceStatus.PAUSED;
        const isUpdatingStatus = meta.options?.isUpdatingStatus ?? false;
        // Archived schedules (inferred from their template being archived —
        // see PRO-10571) can't be paused/resumed, since the template that
        // drives them is no longer active. Delete remains available.
        const isArchived =
            meta.options?.archivedTemplateIds?.has(row.original.templateId) ??
            false;

        if (
            !onDeleteSchedule &&
            !onPauseSchedule &&
            !onResumeSchedule &&
            !onGoToTemplate &&
            !onOpenSchedule
        ) {
            return null;
        }

        return (
            <ViewTableCell captureClicks>
                <HStack gap={8} align="center" justify="end">
                    {onOpenSchedule && (
                        <Button
                            variant="primary"
                            size="sm"
                            className={viewTableStyles.showOnHover}
                            onClick={() => onOpenSchedule(scheduleId)}
                        >
                            Open
                        </Button>
                    )}
                    <Menu.More className={viewTableStyles.showOnHover}>
                        {!isArchived &&
                            (isPaused
                                ? onResumeSchedule && (
                                      <Menu.Item
                                          disabled={isUpdatingStatus}
                                          onClick={() =>
                                              onResumeSchedule(scheduleId)
                                          }
                                      >
                                          Resume
                                      </Menu.Item>
                                  )
                                : onPauseSchedule && (
                                      <Menu.Item
                                          disabled={isUpdatingStatus}
                                          onClick={() =>
                                              onPauseSchedule(scheduleId)
                                          }
                                      >
                                          Pause
                                      </Menu.Item>
                                  ))}
                        {goToTemplateItem}
                        {onDeleteSchedule && (
                            <Menu.Item
                                onClick={() => onDeleteSchedule(scheduleId)}
                                destructive
                            >
                                Delete
                            </Menu.Item>
                        )}
                    </Menu.More>
                </HStack>
            </ViewTableCell>
        );
    },
);

ServiceTemplateScheduleActionsCell.displayName =
    "ServiceTemplateScheduleActionsCell";
